import React, { createContext, useState, useEffect, useMemo, useCallback, ReactNode, Dispatch, SetStateAction, useContext, useRef } from 'react';
import Cookies from 'js-cookie';

type User = Record<string, any> | null;

interface UserContextType {
    user: User;
    setUser: Dispatch<SetStateAction<User>>;
    refreshUser: () => void;
}

export const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
    children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User>(() => {
        const cookieUser = Cookies.get('user');
        return cookieUser ? JSON.parse(cookieUser) : null;
    });

    const lastCookieRef = useRef<string | null>(null);

    const refreshUser = useCallback(() => {
        const cookieUser = Cookies.get('user');
        const newUser = cookieUser ? JSON.parse(cookieUser) : null;
        // Same content keeps the same object. This runs on every cross-tab
        // storage event (e.g. the idle timer's activity writes), and a fresh
        // object each time re-ran everything keyed on `user` - with the
        // Transactions page open in two tabs, it refetched every few seconds.
        setUser(prev => (JSON.stringify(prev) === JSON.stringify(newUser) ? prev : newUser));
        lastCookieRef.current = cookieUser || null;
    }, []);

    useEffect(() => {
        // Set initial cookie reference
        lastCookieRef.current = Cookies.get('user') || null;

        // Check for cookie changes every 1 second
        const interval = setInterval(() => {
            const currentCookie = Cookies.get('user') || null;
            
            if (currentCookie !== lastCookieRef.current) {
                console.log('Cookie changed from:', lastCookieRef.current, 'to:', currentCookie);
                refreshUser();
            }
        }, 1000);

        const syncUser = () => {
            refreshUser();
        };

        window.addEventListener('storage', syncUser);
        
        return () => {
            clearInterval(interval);
            window.removeEventListener('storage', syncUser);
        };
    }, []);

    // Memoized so consumers depending on the whole context object (rather than
    // just `user`) don't see a "changed" reference on every UserProvider render
    // — an unmemoized value here was cascading into unrelated re-render loops
    // in deeply-nested consumers (e.g. add-provision.tsx's effects keyed on `user`).
    const contextValue = useMemo(() => ({ user, setUser, refreshUser }), [user, refreshUser]);

    return (
        <UserContext.Provider value={contextValue}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
